import axios from "axios";
import { cacheClients, getCachedClients } from "@/lib/offlineDb";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  timeout: 60000,
  withCredentials: true, // send the httpOnly JWT cookie on every request
});

// API helpers
export const authApi = {
  login: (username, password) =>
    api.post("/auth/login", { username, password }).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
};

export const clientsApi = {
  list: (params = {}) =>
    api
      .get("/clients", { params })
      .then((r) => {
        // Cache the unfiltered list (without search/filter) for offline access
        if (!params.search) {
          cacheClients(r.data).catch(() => {});
        }
        return r.data;
      })
      .catch(async (err) => {
        // Network failure → fall back to offline cache
        if (!navigator.onLine || err?.code === "ERR_NETWORK") {
          const cached = await getCachedClients();
          if (params.search) {
            const q = String(params.search).toLowerCase().trim();
            return cached.filter((c) =>
              [c.nom, c.prenom, c.numero_client, c.email, c.telephone, c.ville]
                .filter(Boolean)
                .some((v) => String(v).toLowerCase().includes(q)),
            );
          }
          return cached;
        }
        throw err;
      }),
  archived: () => api.get("/clients/archived").then((r) => r.data),
  get: (id) => api.get(`/clients/${id}`).then((r) => r.data),
  create: (data) => api.post("/clients", data).then((r) => r.data),
  update: (id, data) => api.put(`/clients/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/clients/${id}`).then((r) => r.data),
  removePermanent: (id) =>
    api.delete(`/clients/${id}/permanent`).then((r) => r.data),
  restore: (id) => api.post(`/clients/${id}/restore`).then((r) => r.data),
  bulkDelete: (ids) =>
    api.post("/clients/bulk-delete", { ids }).then((r) => r.data),
  bulkDeletePermanent: (ids) =>
    api.post("/clients/bulk-delete-permanent", { ids }).then((r) => r.data),
  importPreview: (formData) =>
    api
      .post("/clients/import/preview", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),
  importRun: (formData) =>
    api
      .post("/clients/import/run", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data),
};

export const operationsApi = {
  list: (params = {}) => api.get("/operations", { params }).then((r) => r.data),
  get: (id) => api.get(`/operations/${id}`).then((r) => r.data),
  create: (data) => api.post("/operations", data).then((r) => r.data),
  remove: (id) => api.delete(`/operations/${id}`).then((r) => r.data),
  sendEmail: (id, payload) =>
    api.post(`/operations/${id}/send-email`, payload).then((r) => r.data),
  getMedia: (operationId) =>
    api.get(`/media/operation/${operationId}`).then((r) => r.data),
};

export const adminApi = {
  users: () => api.get("/admin/users").then((r) => r.data),
  createUser: (data) => api.post("/admin/users", data).then((r) => r.data),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data).then((r) => r.data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`).then((r) => r.data),
  validateUser: (id) => api.patch(`/admin/users/${id}/validate`).then((r) => r.data),
  icons: () => api.get("/admin/icons").then((r) => r.data),
  updateIcons: (data) => api.post("/admin/icons", data).then((r) => r.data),
  // Public branding endpoint (no auth required, used by Login + PWA manifest)
  publicBranding: () => api.get("/branding").then((r) => r.data),
  backupEmails: () => api.get("/admin/backup-emails").then((r) => r.data),
  updateBackupEmails: (data) =>
    api.post("/admin/backup-emails", data).then((r) => r.data),
  purgeSettings: () => api.get("/admin/purge/settings").then((r) => r.data),
  updatePurgeSettings: (data) =>
    api.put("/admin/purge/settings", data).then((r) => r.data),
  previewPurge: (overrides) =>
    api.post("/admin/purge/preview", overrides || {}).then((r) => r.data),
  runPurge: (overrides) =>
    api.post("/admin/purge/run", overrides || {}).then((r) => r.data),
  previewOrphans: () =>
    api.post("/admin/cleanup-orphans/preview").then((r) => r.data),
  runOrphans: () => api.post("/admin/cleanup-orphans/run").then((r) => r.data),
};

export const searchApi = {
  search: (params) => api.get("/search", { params }).then((r) => r.data),
};

export const savedEmailsApi = {
  list: () => api.get("/saved-emails").then((r) => r.data),
  create: (data) => api.post("/saved-emails", data).then((r) => r.data),
  update: (id, data) => api.put(`/saved-emails/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/saved-emails/${id}`).then((r) => r.data),
};
